import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LockOpen, Save, CheckCircle2, DoorOpen, Beer } from "lucide-react";
import { API_BASE } from "../../lib/site";

const ICONS = { porta: DoorOpen, cervejeira: Beer };

export default function AdminTravas() {
    const [travas, setTravas] = useState([]);
    const [mensagens, setMensagens] = useState({});
    const [msg, setMsg] = useState(null);

    const load = () =>
        axios.get(`${API_BASE}/travas`).then(({ data }) => {
            setTravas(data);
            setMensagens(Object.fromEntries(data.map((t) => [t.key, t.mensagem])));
        }).catch(() => {});

    useEffect(() => {
        load();
    }, []);

    const toggle = async (t) => {
        await axios.put(`${API_BASE}/travas/${t.key}`, { bloqueada: !t.bloqueada }, { withCredentials: true });
        setMsg({ ok: true, text: `${t.nome}: ${t.bloqueada ? "liberada" : "bloqueada"}!` });
        load();
    };

    const toggle18 = async (t) => {
        await axios.put(`${API_BASE}/travas/${t.key}`, { bloqueada: t.bloqueada, exige_maioridade: !t.exige_maioridade }, { withCredentials: true });
        load();
    };

    const salvarMensagem = async (t) => {
        await axios.put(`${API_BASE}/travas/${t.key}`, { bloqueada: t.bloqueada, mensagem: mensagens[t.key] }, { withCredentials: true });
        setMsg({ ok: true, text: `Mensagem de "${t.nome}" salva!` });
    };

    return (
        <section id="admin-travas" data-testid="admin-travas-section" className="scroll-mt-28">
            <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-[#0E6B3F]" />
                <h2 className="text-2xl font-extrabold">Travas da loja</h2>
            </div>
            <p className="mt-2 text-sm text-[#1A231E]/60 font-semibold">
                Bloqueie ou libere a porta de entrada e a cervejeira 18+ com um toque. O app do cliente reflete na hora.
            </p>

            {msg && (
                <p data-testid="admin-travas-message" className={`mt-4 rounded-xl px-4 py-3 text-sm font-semibold border-2 ${msg.ok ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}>
                    {msg.ok ? <CheckCircle2 className="w-4 h-4 inline mr-1.5 -mt-0.5" /> : null}
                    {msg.text}
                </p>
            )}

            <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {travas.map((t) => {
                    const Icon = ICONS[t.key] || Lock;
                    return (
                        <div
                            key={t.key}
                            data-testid={`admin-trava-${t.key}`}
                            className={`rounded-[1.75rem] border-2 p-6 ${t.bloqueada ? "bg-[#EFEAE0] border-[#F57A3A]" : "bg-white border-[#1A231E]/12"} sticker-soft`}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <span className={`w-11 h-11 rounded-2xl grid place-items-center ${t.bloqueada ? "bg-[#F57A3A] text-white" : "bg-[#0E6B3F] text-[#F1C75B]"}`}>
                                        <Icon className="w-6 h-6" />
                                    </span>
                                    <div>
                                        <h3 className="font-extrabold">{t.nome}</h3>
                                        <span className={`text-[10px] font-extrabold uppercase tracking-wider ${t.bloqueada ? "text-[#F57A3A]" : "text-[#0E6B3F]"}`}>
                                            {t.bloqueada ? "Bloqueada" : "Liberada"}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => toggle(t)}
                                    data-testid={`trava-toggle-${t.key}`}
                                    aria-label={`Alternar ${t.nome}`}
                                    className={`w-14 h-8 rounded-full p-1 transition-colors ${t.bloqueada ? "bg-[#F57A3A]" : "bg-[#0E6B3F]"}`}
                                >
                                    <span className={`block w-6 h-6 rounded-full bg-white shadow transition-transform ${t.bloqueada ? "translate-x-6" : ""}`} />
                                </button>
                            </div>

                            <div className="mt-4 flex items-center justify-between gap-3">
                                <span className="text-sm font-bold text-[#1A231E]/70">Exigir maioridade (18+)</span>
                                <button
                                    onClick={() => toggle18(t)}
                                    data-testid={`trava-18-toggle-${t.key}`}
                                    aria-label={`Maioridade ${t.nome}`}
                                    className={`w-12 h-7 rounded-full p-1 transition-colors ${t.exige_maioridade ? "bg-[#0E6B3F]" : "bg-[#1A231E]/15"}`}
                                >
                                    <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${t.exige_maioridade ? "translate-x-5" : ""}`} />
                                </button>
                            </div>

                            <label className="block mt-4 text-xs font-extrabold uppercase tracking-wider text-[#1A231E]/50 mb-1.5">
                                Mensagem quando bloqueada
                            </label>
                            <div className="flex gap-2">
                                <input
                                    data-testid={`trava-mensagem-${t.key}`}
                                    value={mensagens[t.key] ?? ""}
                                    onChange={(e) => setMensagens({ ...mensagens, [t.key]: e.target.value })}
                                    placeholder="Ex.: Manutenção até às 18h"
                                    className="flex-1 rounded-xl border-2 border-[#1A231E]/12 px-3.5 py-2.5 text-sm font-semibold focus:outline-none focus:border-[#F57A3A]"
                                />
                                <button onClick={() => salvarMensagem(t)} data-testid={`trava-mensagem-save-${t.key}`} aria-label={`Salvar mensagem ${t.nome}`} className="w-10 h-10 rounded-xl grid place-items-center bg-[#0E6B3F] text-white hover:bg-[#0A5531] transition-colors shrink-0">
                                    <Save className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
